// === App Layout — sticky top nav (desktop) + bottom nav bar (mobile) ===

import { useState, useCallback, useEffect, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import {
  Box,
  Paper,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  Divider,
  Typography,
  Button,
  IconButton,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Palette as ThemeIcon,
  SystemUpdateAlt as UpdateIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Home as HomeIcon,
  Menu as MenuIcon,
  BarChart as ReportsIcon,
  ExitToApp as ExitToAppIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useProfile } from '../lib/profile';
import { formatInUserTime } from '../lib/date-utils';
import { useThemeContext, getNavBarGradient } from '../lib/theme-context';
import { SettingsDialog } from './SettingsDialog';
import { ImpersonationBanner } from './ImpersonationBanner';
// import { VoiceAgent } from './VoiceAgent'; // hidden for now — re-enable to restore header mic
import { APP_VERSION } from '../version';
import { useConnectionMonitor } from '../hooks/use-connection-monitor';
import { useVisibleNavGroups } from '../lib/card-access-context';
import { PageSearch } from './PageSearch';


const POLL_INTERVAL_MS = 15 * 1000; // 15s — pick up new deploys near-instantly

function fmtDeployedAt(iso: string, profile: any): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  
  return formatInUserTime(d, profile, {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

interface AppLayoutProps {
  readonly children: ReactNode;
}

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
] as const;

export function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { currentTheme } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  // Sidebar shows only the cards this user's job title allows.
  const visibleNavGroups = useVisibleNavGroups();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  const [latestDeployedAt, setLatestDeployedAt] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  // Phone: the search icon opens the page search across the whole top bar.
  const [searchOpen, setSearchOpen] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Version the user chose "Later" on — suppresses the popup for that version so
  // the frequent poll can't re-nag them every interval while they keep working.
  // A newer deploy (different version) still surfaces.
  const dismissedVersionRef = useRef<string | null>(null);
  const navBarGradient = getNavBarGradient(currentTheme);
  const isWhiteTheme = currentTheme === 'white';

  useConnectionMonitor();

  const checkForUpdate = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`);
      if (!res.ok) return;
      const json = (await res.json()) as { version: string; deployedAt: string };
      if (json.version !== APP_VERSION && json.version !== dismissedVersionRef.current) {
        setLatestVersion(json.version);
        setLatestDeployedAt(json.deployedAt);
        setUpdateAvailable(true);
      }
    } catch {
      // network error — ignore silently
    }
  }, []);

  useEffect(() => {
    // NOTE: in apps/web each route wraps its own <AppLayout>, so this component
    // remounts on every navigation (unlike admin's persistent Outlet layout).
    // A 30s-delayed initial check kept getting reset on navigation and so never
    // fired for an active user — that's why the update popup never appeared on
    // web while it worked on admin. Check immediately on mount instead; the
    // interval below still covers users who stay on a single page.
    void checkForUpdate();
    pollTimerRef.current = setInterval(() => { void checkForUpdate(); }, POLL_INTERVAL_MS);

    // Re-check the moment a backgrounded tab is brought back to the foreground,
    // so a user returning to the app sees a deploy that landed while they were away
    // without waiting for the next poll tick.
    const onVisible = (): void => { if (document.visibilityState === 'visible') void checkForUpdate(); };
    document.addEventListener('visibilitychange', onVisible);

    // Live clock update every second
    const clockTimer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      clearInterval(clockTimer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkForUpdate]);

  const handleSignOut = useCallback(async () => {
    setMenuAnchor(null);
    await signOut();
    navigate('/login');
  }, [signOut, navigate]);

  const initials = user?.email?.charAt(0).toUpperCase() ?? '?';
  const displayEmail = user?.email ?? '';

  const isDashboardActive = location.pathname === '/dashboard';
  const isReportsActive = location.pathname.startsWith('/reports');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 7, sm: 0 } }}>
      {/* ── Sticky top bar ── */}
      <Paper
        elevation={0}
        square
        sx={{
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: currentTheme === 'white' ? '#ffffff' : navBarGradient,
          borderBottom: currentTheme === 'white' ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.08)',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        {searchOpen ? (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PageSearch onDark={!isWhiteTheme} autoFocus onDone={() => setSearchOpen(false)} />
            </Box>
            <IconButton
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
              sx={{ ml: 1, color: isWhiteTheme ? 'text.secondary' : '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </>
        ) : (
        <>
        {/* Logo */}
        <Box
          onClick={() => navigate('/dashboard')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: { sm: 3 } }}
        >
          <img src="/click2gro-logo.png" alt="Click2Gro" style={{ height: 32, width: 'auto', display: 'block' }} />
        </Box>

        {/* Desktop nav links — hidden on mobile (bottom nav handles it) */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, flex: 1 }}>
          {NAV_ITEMS.map(({ label, path, icon }) => {
            const active = location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(path));
            return (
              <Button
                key={path}
                onClick={() => navigate(path)}
                startIcon={icon}
                size="small"
                sx={{
                  color: active 
                    ? (currentTheme === 'white' ? 'primary.main' : '#ffffff') 
                    : (currentTheme === 'white' ? 'text.secondary' : 'rgba(255,255,255,0.72)'),
                  fontWeight: active ? 700 : 500,
                  fontSize: '0.8125rem',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: active 
                    ? (currentTheme === 'white' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.18)') 
                    : 'transparent',
                  textTransform: 'none',
                  '&:hover': { 
                    bgcolor: currentTheme === 'white' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.12)', 
                    color: currentTheme === 'white' ? 'primary.main' : '#ffffff' 
                  },
                }}
              >
                {label}
              </Button>
            );
          })}
          {/* Go to page… (Ctrl+K) — same as ICU Admin */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 2, minWidth: 0 }}>
            <Box sx={{ width: '100%', maxWidth: 420 }}>
              <PageSearch onDark={!isWhiteTheme} />
            </Box>
          </Box>
        </Box>

        {/* Phone: search icon opens the page search across the bar */}
        <IconButton
          onClick={() => setSearchOpen(true)}
          aria-label="Search pages"
          sx={{ display: { xs: 'inline-flex', sm: 'none' }, ml: 'auto', color: isWhiteTheme ? 'text.secondary' : '#ffffff' }}
        >
          <SearchIcon />
        </IconButton>

        {/* Talk button — sibling left of avatar so taps don't open the user menu */}
        {/* Hidden for now — leave code in place to re-enable later */}
        {/* <VoiceAgent /> */}

        {/* Right: Avatar / user menu */}
        <Box
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            '&:hover': { bgcolor: currentTheme === 'white' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.1)' },
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700, color: '#fff' }}>
            {initials}
          </Avatar>
          <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' } }}>
            <Typography variant="body2" fontWeight={600} lineHeight={1.2} sx={{ color: currentTheme === 'white' ? 'text.primary' : '#ffffff' }}>
              {displayEmail}
            </Typography>
            <Typography variant="caption" lineHeight={1.3} sx={{ color: currentTheme === 'white' ? 'text.secondary' : 'rgba(255,255,255,0.75)' }}>
              Company user
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.1 }}>
              <Typography sx={{ fontSize: '0.65rem', opacity: 0.7, lineHeight: 1, color: currentTheme === 'white' ? 'text.secondary' : '#ffffff' }}>
                {APP_VERSION}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', opacity: 0.5, lineHeight: 1, color: currentTheme === 'white' ? 'text.disabled' : '#ffffff' }}>·</Typography>
              <Typography sx={{ fontSize: '0.65rem', opacity: 0.7, lineHeight: 1, color: currentTheme === 'white' ? 'text.secondary' : '#ffffff' }}>
                {formatInUserTime(currentTime, profile, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Typography>
            </Box>
          </Box>
          <ArrowDownIcon fontSize="small" sx={{ color: currentTheme === 'white' ? 'text.secondary' : 'rgba(255,255,255,0.75)', ml: 0.5, display: { xs: 'none', md: 'block' } }} />
        </Box>
        </>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { elevation: 4, sx: { mt: 1, minWidth: 210, borderRadius: 2 } } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" fontWeight={700} noWrap>{displayEmail}</Typography>
            <Typography variant="caption" color="text.secondary">Company user</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>
                {APP_VERSION}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', opacity: 0.6, lineHeight: 1 }}>·</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>
                {formatInUserTime(currentTime, profile, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Typography>
            </Box>
          </Box>
          <Divider />
          <MenuItem onClick={() => { setMenuAnchor(null); setSettingsOpen(true); }}>
            <ListItemIcon><ThemeIcon fontSize="small" /></ListItemIcon>
            Theme
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { void handleSignOut(); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Paper>

      {/* ── Mobile bottom navigation — floating glass pill, theme-aware ── */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed',
          bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1100,
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          py: 1.25,
          minWidth: 320,
          maxWidth: '90vw',
          borderRadius: '32px',
          ...(isWhiteTheme
            ? {
                bgcolor: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              }
            : {
                background: navBarGradient,
                border: '1px solid rgba(255,255,255,0.18)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              }),
        }}
      >
        {/* Home/Dashboard pill (with label) */}
        <Box
          onClick={() => navigate('/dashboard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.75,
            py: 1,
            borderRadius: '24px',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            bgcolor: isDashboardActive
              ? (isWhiteTheme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.18)')
              : 'transparent',
            '&:hover': {
              bgcolor: isWhiteTheme ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.12)',
            },
          }}
        >
          <HomeIcon sx={{ color: isWhiteTheme ? 'text.primary' : '#fff', fontSize: 24 }} />
          <Typography
            variant="body2"
            sx={{
              color: isWhiteTheme ? 'text.primary' : '#fff',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Home
          </Typography>
        </Box>

        {/* Nav — slide-up drawer with all destinations */}
        <IconButton
          onClick={() => setNavDrawerOpen(true)}
          sx={{
            color: isWhiteTheme ? 'text.primary' : '#fff',
            '&:hover': { bgcolor: isWhiteTheme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.18)' },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Reports */}
        <IconButton
          onClick={() => navigate('/reports')}
          sx={{
            color: isReportsActive
              ? 'primary.main'
              : (isWhiteTheme ? 'text.primary' : '#fff'),
            '&:hover': { bgcolor: isWhiteTheme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.18)' },
          }}
        >
          <ReportsIcon />
        </IconButton>

        {/* Sign out */}
        <IconButton
          onClick={() => { void handleSignOut(); }}
          sx={{
            color: '#EF5350',
            '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.12)' },
          }}
        >
          <ExitToAppIcon />
        </IconButton>
      </Paper>

      {/* ── Mobile slide-up nav drawer ── */}
      <SwipeableDrawer
        anchor="bottom"
        open={navDrawerOpen}
        onClose={() => setNavDrawerOpen(false)}
        onOpen={() => setNavDrawerOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh',
            background: isWhiteTheme ? '#ffffff' : navBarGradient,
            color: isWhiteTheme ? 'text.primary' : '#ffffff',
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}>
          {/* Tap-to-close drag handle */}
          <Box
            onClick={() => setNavDrawerOpen(false)}
            sx={{
              py: 1,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 5,
                bgcolor: isWhiteTheme ? 'divider' : 'rgba(255,255,255,0.45)',
                borderRadius: 2.5,
              }}
            />
          </Box>
          {/* Header row: title + explicit close button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: isWhiteTheme ? 'text.primary' : '#ffffff' }}
            >
              Navigate
            </Typography>
            <IconButton
              size="small"
              onClick={() => setNavDrawerOpen(false)}
              aria-label="Close navigation"
              sx={{
                color: isWhiteTheme ? 'text.secondary' : 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: isWhiteTheme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.12)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* Home — always at the top so users can return to Dashboard from anywhere */}
          <ListItemButton
            onClick={() => {
              setNavDrawerOpen(false);
              navigate('/dashboard');
            }}
            sx={{
              borderRadius: 1.5,
              py: 1,
              mb: 1.5,
              '&:hover': {
                bgcolor: isWhiteTheme ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: isWhiteTheme ? 'primary.main' : '#fff' }}>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Home"
              secondary="Return to dashboard"
              primaryTypographyProps={{
                fontWeight: 600,
                fontSize: 14,
                color: isWhiteTheme ? 'text.primary' : '#ffffff',
              }}
              secondaryTypographyProps={{
                fontSize: 12,
                color: isWhiteTheme ? 'text.secondary' : 'rgba(255,255,255,0.65)',
              }}
            />
          </ListItemButton>
          {visibleNavGroups.map((group) => (
            <Box key={group.heading} sx={{ mb: 1.5 }}>
              <Typography
                variant="overline"
                sx={{
                  px: 1,
                  color: isWhiteTheme ? 'text.secondary' : 'rgba(255,255,255,0.7)',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                {group.heading}
              </Typography>
              {group.items.filter((item) => item.available).map((item) => (
                <ListItemButton
                  key={item.path}
                  onClick={() => {
                    setNavDrawerOpen(false);
                    navigate(item.path);
                  }}
                  sx={{
                    borderRadius: 1.5,
                    py: 1,
                    '&:hover': {
                      bgcolor: isWhiteTheme ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: item.color }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.description}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: isWhiteTheme ? 'text.primary' : '#ffffff',
                    }}
                    secondaryTypographyProps={{
                      fontSize: 12,
                      color: isWhiteTheme ? 'text.secondary' : 'rgba(255,255,255,0.65)',
                    }}
                  />
                </ListItemButton>
              ))}
            </Box>
          ))}
        </Box>
      </SwipeableDrawer>

      {/* Superadmin "View as Company" indicator — null for normal users */}
      <ImpersonationBanner />

      {/* ── Page content ── */}
      <Box sx={{ pb: { xs: 3, sm: 0 } }}>{children}</Box>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Update Available Dialog */}
      <Dialog open={updateAvailable} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UpdateIcon color="primary" />
          Update Available
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2">
                A new version of the company portal has been deployed.
              </Typography>
              <Box sx={(theme) => ({
                bgcolor: theme.palette.action.hover,
                borderRadius: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              })}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Current</Typography>
                  <Typography variant="caption" fontWeight={600}>{APP_VERSION}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">New version</Typography>
                  <Typography variant="caption" fontWeight={700} color="primary.main">{latestVersion}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Deployed</Typography>
                  <Typography variant="caption" fontWeight={600}>{fmtDeployedAt(latestDeployedAt, profile)}</Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Click <strong>Update</strong> to reload with the latest version.
              </Typography>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            size="small"
            onClick={() => { dismissedVersionRef.current = latestVersion; setUpdateAvailable(false); }}
            sx={{ color: 'text.secondary' }}
          >
            Later
          </Button>
          <Button variant="contained" startIcon={<UpdateIcon />} onClick={() => { window.location.reload(); }}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
